<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import BookingWidgetRow from './BookingWidgetRow.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const SUPPORTED_LOCALES = ['en', 'fr', 'de', 'nl'];

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();

const creating = ref(false);
const addingLocaleForId = ref(null);
const selectedWidget = ref(null);
const showDeleteModal = ref(false);
const deletingLoading = ref({});
const collapsedIds = ref(new Set());

const loading = computed(
  () => store.getters['bookingWidgets/getUIFlags']?.isFetching || false
);
const widgets = computed(
  () => store.getters['bookingWidgets/getBookingWidgets'] || []
);

const primaryWidgets = computed(() =>
  widgets.value.filter(
    widget => widget.is_primary !== false && !widget.parent_booking_widget_id
  )
);

const localeCopiesByParent = computed(() => {
  const map = new Map();
  widgets.value.forEach(widget => {
    if (!widget.parent_booking_widget_id) return;
    const parentId = String(widget.parent_booking_widget_id);
    if (!map.has(parentId)) map.set(parentId, []);
    map.get(parentId).push(widget);
  });
  map.forEach(list => {
    list.sort((a, b) => String(a.locale).localeCompare(String(b.locale)));
  });
  return map;
});

const childrenFor = widget =>
  localeCopiesByParent.value.get(String(widget.id)) || [];

const missingLocalesFor = widget => {
  const used = new Set([String(widget.locale || 'en').toLowerCase()]);
  childrenFor(widget).forEach(child =>
    used.add(String(child.locale).toLowerCase())
  );
  return SUPPORTED_LOCALES.filter(locale => !used.has(locale));
};

const isExpanded = widget => !collapsedIds.value.has(String(widget.id));

const toggleExpand = widget => {
  const next = new Set(collapsedIds.value);
  const key = String(widget.id);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsedIds.value = next;
};

const tableHeaders = computed(() => [
  t('BOOKING_WIDGETS.LIST.TABLE_HEADER.NAME'),
  t('BOOKING_WIDGETS.LIST.TABLE_HEADER.STATUS'),
  '',
]);

onMounted(() => {
  store.dispatch('bookingWidgets/get');
});

const handleCreate = async () => {
  creating.value = true;
  try {
    const created = await store.dispatch('bookingWidgets/create', {
      name: t('BOOKING_WIDGETS.DEFAULT_NAME'),
    });
    if (created?.id) {
      router.push({
        name: 'settings_booking_widgets_show',
        params: {
          accountId: accountId.value,
          bookingWidgetId: created.id,
        },
      });
    }
  } catch (error) {
    useAlert(error.message || t('BOOKING_WIDGETS.SAVE_ERROR'));
  } finally {
    creating.value = false;
  }
};

const handleEdit = widget => {
  router.push({
    name: 'settings_booking_widgets_show',
    params: {
      accountId: accountId.value,
      bookingWidgetId: widget.id,
    },
  });
};

const handleDelete = widget => {
  selectedWidget.value = widget;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  if (!selectedWidget.value) return;

  deletingLoading.value[selectedWidget.value.id] = true;
  try {
    await store.dispatch('bookingWidgets/delete', selectedWidget.value.id);
    useAlert(t('BOOKING_WIDGETS.DELETE_SUCCESS'));
  } catch {
    useAlert(t('BOOKING_WIDGETS.DELETE_ERROR'));
  } finally {
    deletingLoading.value[selectedWidget.value.id] = false;
    showDeleteModal.value = false;
    selectedWidget.value = null;
  }
};

const cancelDelete = () => {
  showDeleteModal.value = false;
  selectedWidget.value = null;
};

const handleAddLocale = async (widget, locale) => {
  if (!widget?.id || addingLocaleForId.value) return;
  addingLocaleForId.value = widget.id;
  try {
    const created = await store.dispatch('bookingWidgets/createLocaleCopy', {
      id: widget.id,
      locale,
    });
    if (created) {
      const next = new Set(collapsedIds.value);
      next.delete(String(widget.id));
      collapsedIds.value = next;
      await store.dispatch('bookingWidgets/get');
      useAlert(t('BOOKING_WIDGETS.LOCALE_CREATE_SUCCESS'));
    }
  } catch (error) {
    useAlert(error.message || t('BOOKING_WIDGETS.LOCALE_CREATE_ERROR'));
  } finally {
    addingLocaleForId.value = null;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('BOOKING_WIDGETS.LIST.LOADING')"
    :no-records-found="!primaryWidgets.length"
    :no-records-message="$t('BOOKING_WIDGETS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BOOKING_WIDGETS.HEADER')"
        :description="$t('BOOKING_WIDGETS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            md
            :label="$t('BOOKING_WIDGETS.NEW')"
            :is-loading="creating"
            @click="handleCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 rtl:text-right ltr:text-left font-semibold text-n-slate-11 text-sm"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="text-n-slate-11">
          <template
            v-for="widget in primaryWidgets"
            :key="widget.id"
          >
            <BookingWidgetRow
              :widget="widget"
              :loading="deletingLoading[widget.id]"
              :expandable="childrenFor(widget).length > 0"
              :expanded="isExpanded(widget)"
              :missing-locales="missingLocalesFor(widget)"
              :adding-locale="addingLocaleForId === widget.id"
              @edit="handleEdit"
              @delete="handleDelete"
              @toggle-expand="toggleExpand(widget)"
              @add-locale="handleAddLocale(widget, $event)"
            />
            <template v-if="isExpanded(widget)">
              <BookingWidgetRow
                v-for="child in childrenFor(widget)"
                :key="child.id"
                nested
                :widget="child"
                :loading="deletingLoading[child.id]"
                @edit="handleEdit"
                @delete="handleDelete"
              />
            </template>
          </template>
        </tbody>
      </table>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-confirm="confirmDelete"
      :title="$t('BOOKING_WIDGETS.DELETE_CONFIRM.TITLE')"
      :message="$t('BOOKING_WIDGETS.DELETE_CONFIRM.MESSAGE')"
      :message-value="selectedWidget?.name"
      :confirm-text="$t('BOOKING_WIDGETS.DELETE_CONFIRM.YES')"
      :reject-text="$t('BOOKING_WIDGETS.DELETE_CONFIRM.NO')"
      @close="cancelDelete"
    />
  </SettingsLayout>
</template>
